import { useContext } from 'react'

import {
  deepSetObject, digObject, toggleArray,
} from '@domain-group/javascript-utils'

import {
  Button,
  Form,
  ModalContext,
  SidebarModal,
  SidebarModalListItem,
} from '@domain-group/suit-theme'

import adminMemberRoles from '@functions/teamMember'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useUser, { useUserForm } from '@hooks/useUser'

const MODAL_KEY = 'ManageUserTeamMemberRolesModal'

const toggleMemberRole = (userForm: ReturnType<typeof useUserForm>, key: string) => {
  const {
    entityState: {
      data,
    },
    setEntityState,
  } = userForm

  const allowedTeamMemberRoles = digObject(data, 'allowed_team_member_roles', [])
  const updatedAllowedRoles = toggleArray(allowedTeamMemberRoles, key)
  // @ts-expect-error TS2345
  const updatedData = deepSetObject(data, 'allowed_team_member_roles', updatedAllowedRoles)

  setEntityState({ data: updatedData })
}

type ModalCallbacks = {
  closeModal: () => void,
  updateUser: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageUserTeamMemberRolesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateUser } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { user: initUser } = digObject(modalData, MODAL_KEY, {})

  const userPayload = useUser(initUser)
  const {
    callbacks: {
      updateUser: updateFn,
    },
    updating,
    user,
  } = userPayload

  const userForm = useUserForm(user)
  const {
    entityState,
    entityState: {
      data,
    },
  } = userForm

  const allowedTeamMemberRoles = digObject(data, 'allowed_team_member_roles', [])

  const editTaskSettingsPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Team Member Roles Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Team Member"
        titleSecondLine="Roles"
      />

      <Form.Field label="Available Roles" labelProps={{ marginLeft: 'large' }} marginTop="large">
        {adminMemberRoles.map((teamMemberRole) => {
          const { key, title } = teamMemberRole

          return (
            <SidebarModalListItem
              boxProps={{ paddingLeft: 'large' }}
              hideAngleIcon
              key={key}
              isChecked={allowedTeamMemberRoles.includes(key)}
              onClick={() => toggleMemberRole(userForm, key)}
              showCheckbox
              title={title}
            />
          )
        })}
      </Form.Field>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateUser(editTaskSettingsPayload)}
          size="large"
        >
          Save Team Member Roles
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageUserTeamMemberRolesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
